import { useCallback, useEffect, useMemo, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import SettingsLayout from "screens/Settings/components/SettingsLayout";
import { getAllPlansData } from "screens/Settings/MyPlan/MyPlanFunctions";
import {
  buildSeatHint,
  buildUpgradePlanCards,
  findMatchingPlanId,
  getAnnualSavingsPercent,
  getPlanSale,
  UpgradePlanPromo,
} from "screens/Settings/MyPlan/components/UpgradePlanHelper";
import {
  buildCheckoutSummary,
  CheckoutType,
} from "screens/Settings/MyPlan/components/CheckoutHelper";
import {
  handleErrorMessage,
  isUnlimitedLimit,
  openErrorNotification,
} from "helpers/methods";
import notifiers from "global/constants/NotificationConstants";
import useBillingSummary from "hooks/use-billing-summary";
import useBillingConfig from "hooks/use-billing-config";
import usePromoCampaign from "hooks/use-promo-campaign";
import upgradePlanTokens from "screens/Settings/MyPlan/UpgradePlan.tokens";
import urls from "global/constants/UrlConstants";
import { fetchPlanSwitchPreview, fetchSeatCheck } from "Services/CommonServices";
import {
  clampSeatsToCap,
  getPlanSeatLimit,
  getSeatReductionFromError,
  getSeatUsage,
  isPerSeatPlan,
  needsSeatReduction,
  SeatBlockReason,
} from "helpers/perSeatHelpers";
import { PlanSwitchPreview, SeatReductionRequired } from "types/perSeat";
import CustomSeatReductionDialog from "global/components/CustomSeatReductionDialog/CustomSeatReductionDialog";
import CustomSeatBlockedDialog from "global/components/CustomSeatBlockedDialog/CustomSeatBlockedDialog";
import OnboardingStep1 from "./OnboardingStep1";
import PlanCheckout from "./PlanCheckout";

const t = upgradePlanTokens;

const UpgardePlan = (props: {
  currentPlan: any;
  accountStats: any;
  cardData: any;
  getUserData: Function;
  setUpgradePlanScreen: Function;
  setLoading: Function;
  loader: boolean;
  /**
   * Whether a paid subscription already exists. Without one the purchase is a
   * first invoice billed in full, so checkout must not quote a prorated switch.
   */
  isSubscriptionActive?: boolean;
}) => {
  const [step, setStep] = useState<number>(0);
  const promise = useMemo(() => loadStripe(urls.STRIPE_PUBLIC_KEY), []);
  const [selectedPlan, setSelectedPlan] = useState<any>({});
  const [checkoutType, setCheckoutType] = useState<CheckoutType>("switch");
  const [allPlansData, setAllPlansData] = useState<any>({});
  const [isAnnual, setIsAnnual] = useState<boolean>(false);
  const [seats, setSeats] = useState<number>(t.seats.min);

  /** Also the retry offered when the catalog comes back with no plans. */
  const loadPlans = useCallback(() => {
    getAllPlansData(setAllPlansData, props.setLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  /**
   * Seats the account already has: `used` are occupied (the floor — you can't
   * buy fewer than the people already in them) and `limit` is what's paid for
   * today, which seeds the counter.
   */
  const seatUsage = useMemo(
    () => getSeatUsage(props.accountStats),
    [props.accountStats],
  );
  const minSeats = Math.max(t.seats.min, seatUsage.used);

  // Credit left on the account by earlier downgrades or seat releases; Stripe
  // spends it on this invoice before it touches the card.
  const { summary: billingSummary, isLoading: isCreditLoading } =
    useBillingSummary(Boolean(props.isSubscriptionActive));
  // The app-level gate; the plan's own metadata is the second half of it.
  const { perSeatBillingEnabled: isPerSeatApp, offerEnabled } =
    useBillingConfig();

  /*
    Seasonal promotion. The strip and the cycle pills follow the campaign
    calendar. A card's sale price comes from the catalog (`discountPrice`, set
    from the plan's dated Stripe promotion code), and whenever the catalog
    carries one it is shown — for every account, whatever its subscription.
  */
  const { campaign: promoCampaign, isPricePreview } = usePromoCampaign();
  const promo = useMemo<UpgradePlanPromo | null>(
    () =>
      promoCampaign
        ? {
            campaign: promoCampaign,
            preview: isPricePreview,
            isChargeable: () => true,
          }
        : null,
    [promoCampaign, isPricePreview],
  );

  /**
   * Seats paid for today; they carry over unchanged through a plan switch.
   *
   * An uncapped plan stores a sentinel limit (~100,000) rather than a real
   * purchased count. Multiplying that by a price produces nonsense, so fall
   * back to the seats actually in use.
   */
  const purchasedSeats = isUnlimitedLimit(seatUsage.limit)
    ? minSeats
    : Math.max(minSeats, seatUsage.limit);

  useEffect(() => {
    setSeats(purchasedSeats);
  }, [purchasedSeats]);

  /**
   * What the customer is buying — quoted at checkout and sent with it.
   *
   * Not floored at `minSeats`: picking fewer seats than there are active users
   * is allowed here too, same as "Manage seats" — `handleSelectPlan` runs the
   * seat check against this exact number, so going below the active count
   * surfaces the keep-picker instead of silently buying more than asked for.
   */
  const checkoutSeats = seats;

  /**
   * The count to buy on a given plan: the dialled-in seats, held under that
   * plan's own cap. Read from the *catalog* limit rather than the picker's
   * `getPlanSeatCap`, whose "no cap means one seat" stand-in is a display rule
   * — applying it to a purchase would bill an uncapped plan for a single seat.
   */
  const getSeatsToBuy = (plan: any) =>
    clampSeatsToCap(checkoutSeats, getPlanSeatLimit(plan));

  const monthlyPlans: any[] = useMemo(
    () => allPlansData?.Monthly ?? [],
    [allPlansData],
  );
  const annualPlans: any[] = useMemo(
    () => allPlansData?.Annual ?? [],
    [allPlansData],
  );
  const cyclePlans = isAnnual ? annualPlans : monthlyPlans;

  const currentPlanId = useMemo(
    () => findMatchingPlanId(cyclePlans, props.currentPlan),
    [cyclePlans, props.currentPlan],
  );

  const cards = useMemo(
    () =>
      buildUpgradePlanCards({
        plans: cyclePlans,
        isAnnual,
        seats,
        currentPlanId,
        isPerSeatApp,
        promo,
      }),
    [cyclePlans, isAnnual, seats, currentPlanId, isPerSeatApp, promo],
  );

  const annualSavingsPercent = getAnnualSavingsPercent(annualPlans);

  /** Downgrade that needs the customer to pick who stays. */
  const [seatReduction, setSeatReduction] =
    useState<SeatReductionRequired | null>(null);
  const [keepUserIds, setKeepUserIds] = useState<number[] | undefined>();
  const [seatBlock, setSeatBlock] = useState<{
    reason: SeatBlockReason;
    message?: string;
  } | null>(null);
  /** Plan whose seat check is in flight — drives that card's spinner. */
  const [checkingPlanId, setCheckingPlanId] = useState<string>("");

  /**
   * The live cost of the switch under review at checkout — authoritative once
   * it lands, replacing the client-side estimate `buildCheckoutSummary` would
   * otherwise compute. Fetched for every "switch" checkout: the endpoint
   * covers a first purchase too (it answers `prorated: false`, full price —
   * see `PlanSwitchPreview`), so this isn't gated on `isSubscriptionActive`.
   */
  const [planPreview, setPlanPreview] = useState<PlanSwitchPreview | undefined>();
  const [isPreviewLoading, setPreviewLoading] = useState<boolean>(false);

  useEffect(() => {
    if (step !== 1 || checkoutType !== "switch" || !selectedPlan?.id) {
      setPlanPreview(undefined);
      // A fetch from a previous, still-valid render can be mid-flight here —
      // its own `finally` is about to be disarmed by `cancelled = true` below,
      // so nothing else would ever clear this otherwise.
      setPreviewLoading(false);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    fetchPlanSwitchPreview(selectedPlan.id, getSeatsToBuy(selectedPlan), keepUserIds)
      .then((response) => {
        if (!cancelled) setPlanPreview(response);
      })
      .catch((error: any) => {
        if (cancelled) return;
        // Same shape the switch itself would answer with — drop back to the
        // keep-picker rather than quoting a total for a switch that can't go
        // through as dialled in.
        const reduction = getSeatReductionFromError(error);
        if (reduction) {
          setSeatReduction(reduction);
          setStep(0);
          return;
        }
        // Advisory: the switch itself still prices and enforces the change,
        // so a failed preview falls back to the client-side estimate rather
        // than blocking checkout.
        handleErrorMessage(error);
        setPlanPreview(undefined);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [step, checkoutType, selectedPlan?.id, seats, keepUserIds]);

  const openCheckout = (plan: any, type: CheckoutType, keep?: number[]) => {
    setSelectedPlan(plan);
    setCheckoutType(type);
    setKeepUserIds(keep);
    setStep(1);
  };

  /**
   * A downgrade may leave the account over the target plan's user limit, so ask
   * the API first and collect the users to keep before taking any payment.
   * Upgrades and same-size switches never come back with users.
   */
  const handleSelectPlan = async (
    plan: any,
    type: CheckoutType = "switch",
  ) => {
    if (!plan?.id) return;
    try {
      setCheckingPlanId(plan.id);
      // Ask against the seats being bought, not just the plan's cap: buying
      // fewer seats than there are users needs a reduction too.
      const check = await fetchSeatCheck(plan.id, getSeatsToBuy(plan));
      if (needsSeatReduction(check)) {
        setSelectedPlan(plan);
        setCheckoutType(type);
        setSeatReduction(check);
        return;
      }
      openCheckout(plan, type);
    } catch (error: any) {
      // The check is advisory — the switch itself still enforces the rule and
      // answers 409, so a failure here shouldn't block the flow.
      handleErrorMessage(error);
      openCheckout(plan, type);
    } finally {
      setCheckingPlanId("");
    }
  };

  /** Seat prompts are reachable from both steps, so they render in each. */
  const getSeatDialogs = () => (
    <>
      <CustomSeatReductionDialog
        reduction={seatReduction}
        // The customer keeps as many people as they have seats, not as many as
        // the plan's cap would allow — and the seats being bought are already
        // held under that cap.
        purchasedSeats={getSeatsToBuy(selectedPlan)}
        onClose={() => setSeatReduction(null)}
        onConfirm={(ids) => {
          setSeatReduction(null);
          openCheckout(selectedPlan, checkoutType, ids);
        }}
      />
      <CustomSeatBlockedDialog
        reason={seatBlock?.reason ?? null}
        message={seatBlock?.message}
        onClose={() => setSeatBlock(null)}
        onUpgrade={() => setStep(0)}
        onUpdateCard={() => props.setUpgradePlanScreen(false)}
      />
    </>
  );

  /** The sale checkout quotes and asks the backend to apply, if any. */
  const selectedSale = getPlanSale(selectedPlan, isAnnual, promo);

  if (step === 1) {
    return (
      <SettingsLayout activeTab="My Plan" title="Checkout">
        <Elements stripe={promise}>
          <PlanCheckout
            summary={buildCheckoutSummary({
              type: checkoutType,
              plan: selectedPlan,
              currentPlan: props.currentPlan,
              isAnnual,
              // Buy-upfront: the seats dialled in on the previous step are what
              // gets bought, on a first purchase and on a switch alike — both
              // endpoints take the count, and the backend clamps it to the
              // target plan's cap.
              seats: getSeatsToBuy(selectedPlan),
              currentSeats: purchasedSeats,
              isPerSeat: isPerSeatApp && isPerSeatPlan(selectedPlan),
              // An offer schedule in play takes the same cancel-and-recreate
              // path as a non-per-seat switch — no proration either way.
              offerEnabled,
              validTill: props.accountStats?.validTill,
              annualSavingsPercent,
              hasActiveSubscription: Boolean(props.isSubscriptionActive),
              // Saved credit is spent before the card is, so the order has to
              // account for it or the total won't match the invoice.
              accountCredit: billingSummary?.creditBalance ?? 0,
              // Authoritative once it lands; falls back to the client-side
              // estimate above while it's loading or if the call failed.
              preview: planPreview,
              sale: selectedSale,
            })}
            selectedPlan={selectedPlan}
            isPlanAnnual={isAnnual}
            promoCampaign={selectedSale ? promoCampaign : null}
            // The order's totals are net of saved credit and, on a switch, of
            // the live preview — they stay in a loading state until both land
            // rather than quoting a figure that's about to change.
            isSummaryLoading={isCreditLoading || isPreviewLoading}
            seats={getSeatsToBuy(selectedPlan)}
            // Decides whether the purchase carries a seat count at all: a
            // flat-billed plan is quantity 1 and takes no seats field.
            isPerSeat={isPerSeatApp && isPerSeatPlan(selectedPlan)}
            cardData={props.cardData ?? []}
            getUserData={props.getUserData}
            keepUserIds={keepUserIds}
            onSeatReductionRequired={(reduction) => {
              setSeatReduction(reduction);
              setStep(0);
            }}
            onKeepSelectionStale={(message) => {
              openErrorNotification(message ?? notifiers.GENERIC_ERROR);
              // The same pre-flight the plan was chosen with, run against a
              // fresh roster: it re-opens the picker when a reduction is still
              // needed, and drops straight back to checkout when it isn't.
              setKeepUserIds(undefined);
              handleSelectPlan(selectedPlan, checkoutType);
            }}
            onSeatBlocked={(reason, message) =>
              setSeatBlock({ reason, message })
            }
            onBack={() => setStep(0)}
            onDone={() => props.setUpgradePlanScreen(false)}
          />
        </Elements>
        {getSeatDialogs()}
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout activeTab="My Plan" title="Choose your plan">
      <OnboardingStep1
        isLoading={props.loader}
        seats={seats}
        seatHint={buildSeatHint(cyclePlans, seats)}
        onSeatChange={setSeats}
        isAnnual={isAnnual}
        onCycleChange={(value: boolean) => {
          setIsAnnual(value);
          setSelectedPlan({});
        }}
        annualSavingsPercent={annualSavingsPercent}
        cards={cards}
        promoCampaign={promoCampaign}
        // The counter is the purchase quantity: it drives eligibility, the
        // recommendation, the per-seat maths on the cards, and it is what gets
        // billed. Changing seats on an existing subscription happens through
        // "Manage seats", not here, so there is no banner.
        seatUpdate={null}
        onSeatUpdate={() => undefined}
        onSelectPlan={(plan: any) => handleSelectPlan(plan, "switch")}
        onBack={() => props.setUpgradePlanScreen(false)}
        onRetry={loadPlans}
        isBusy={Boolean(checkingPlanId)}
        checkingPlanId={checkingPlanId}
      />
      {getSeatDialogs()}
    </SettingsLayout>
  );
};

export default UpgardePlan;
